import { refreshSession, logoutSession, clearAccessSession, isExplicitlyLoggedOut, SessionChange } from '../service/sessionProtocol';
import { rememberProfile } from '../../auth/utils/recentProfiles';
import { isCanonicalAuthRejection } from '../service/authRejection';
import { readStoredAccessToken, writeAccessToken } from '../service/requestCredential';
import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { apiService } from '../service/api';
import { UserBasic } from '../types';
import { connectNotificationsWS, disconnectWS } from "../../shared/realtime/ws";
import { getWebSocketBaseUrl } from '../../../config/api';
import { reconnectMonitoringWebSocket } from '../../production/control/monitoramento/hooks/useMonitoringWebSocket';
import { queryCache } from '../../../utils/queryCache';
import { clearConsultoriaSession } from '../../consultoria/services/consultoriaSession';
import { getRateLimitInfo } from '../service/rateLimit';

interface AuthContextType {
  user: UserBasic | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const getWSBaseUrl = () => getWebSocketBaseUrl();

// Centraliza encerramento de sessão sem depender de estado React.
// Seguro para uso dentro de callbacks assíncronos (sem risco de closure estale).
function clearSessionAndRedirect(reason: string): void {
  clearAccessSession();
  writeAccessToken(null);
  localStorage.removeItem('user');
  localStorage.removeItem('user_data');
  localStorage.removeItem('sistemat-tabs');
  queryCache.clear();
  clearConsultoriaSession();
  disconnectWS();
  window.location.href = `/login?reason=${reason}`;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const identityEpoch = useRef(0);
  const [user, setUser] = useState<UserBasic | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [refreshIntervalId, setRefreshIntervalId] = useState<number | null>(null);

  const login = async (email: string, password: string): Promise<boolean> => {
    const epoch = ++identityEpoch.current;
    setIsLoading(true);
    try {
      const authResponse = await apiService.login({ email, password });

      if (epoch !== identityEpoch.current) return false;
      const loggedUser: UserBasic = {
        id: authResponse.user.id,
        email: authResponse.user.email,
        full_name: authResponse.user.full_name,
        user_level: authResponse.user.user_level,
        company_id: authResponse.user.company_id,
        company_name: authResponse.user.company_name,
        is_active: authResponse.user.is_active,
      };

      setUser(loggedUser);
      localStorage.setItem('user', JSON.stringify(loggedUser));
      writeAccessToken(authResponse.access_token);
      rememberProfile({ email: loggedUser.email, full_name: loggedUser.full_name });
      queryCache.clear();
      connectNotificationsWS(getWSBaseUrl());

      return true;
    } catch (error: any) {
      console.error('❌ Erro no login:', error.response?.status, error.response?.data);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    identityEpoch.current += 1;
    setUser(null);
    void logoutSession().catch(() => { window.dispatchEvent(new Event('mestti-logout-unconfirmed')); });
    writeAccessToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('user_data');
    localStorage.removeItem('sistemat-tabs');
    queryCache.clear();
    clearConsultoriaSession();
    disconnectWS();
    if (refreshIntervalId !== null) {
      window.clearInterval(refreshIntervalId);
      setRefreshIntervalId(null);
    }
  };

  // Only a fresh server response can establish the restored identity.
  useEffect(() => {
    let disposed = false;
    localStorage.removeItem('access_token'); // One-time legacy credential cleanup.
    const bootstrap = async () => {
      const epoch = identityEpoch.current;
      if (isExplicitlyLoggedOut()) { setIsInitialized(true); return; }
      try {
        const data = await refreshSession();
        if (disposed || epoch !== identityEpoch.current || isExplicitlyLoggedOut()) return;
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
        connectNotificationsWS(getWSBaseUrl());
      } catch {
        if (!disposed && epoch === identityEpoch.current) { setUser(null); clearAccessSession(); }
      } finally {
        if (!disposed) setIsInitialized(true);
      }
    };
    const identityChanged = (event: Event) => {
      const change = (event as CustomEvent<SessionChange>).detail;
      if (change?.type !== 'LOGIN_CHANGED' && change?.type !== 'LOGOUT') return;
      identityEpoch.current += 1;
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('user_data');
      localStorage.removeItem('sistemat-tabs');
      queryCache.clear(); clearConsultoriaSession(); disconnectWS();
      if (change.type === 'LOGIN_CHANGED') void bootstrap();
      else setIsInitialized(true);
    };
    void bootstrap();
    window.addEventListener('mestti-session-changed', identityChanged);
    return () => { disposed = true; window.removeEventListener('mestti-session-changed', identityChanged); };
  }, []);

  // Renovação automática do token
  useEffect(() => {
    if (!user) {
      if (refreshIntervalId !== null) {
        window.clearInterval(refreshIntervalId);
        setRefreshIntervalId(null);
      }
      return;
    }

    const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

    const id = window.setInterval(async () => {
      const epoch = identityEpoch.current;
      try {
        const token = readStoredAccessToken();
        if (!token) return;

        const data = await apiService.refreshToken();
        if (epoch !== identityEpoch.current || isExplicitlyLoggedOut()) return;
        if (data?.access_token) {
          writeAccessToken(data.access_token);
          disconnectWS();
          connectNotificationsWS(getWSBaseUrl());
          reconnectMonitoringWebSocket();
        }
      } catch (error) {
        if (epoch !== identityEpoch.current) return;
        if (!isCanonicalAuthRejection(error)) {
          // 429, 5xx, rede, timeout: a sessão continua válida. O próximo ciclo
          // rearmado; o refresh é independente do prazo do access token.
          const rateLimit = getRateLimitInfo(error);
          console.warn(
            rateLimit
              ? `Renovação de token adiada pelo servidor (${rateLimit.retryAfterSeconds}s).`
              : 'Renovação de token falhou; sessão preservada.',
            (error as any)?.response?.status,
          );
          return;
        }

        console.warn('Credencial recusada ao renovar sessão');
        clearSessionAndRedirect('session_expired');
      }
    }, REFRESH_INTERVAL_MS);

    setRefreshIntervalId(id);

    return () => {
      window.clearInterval(id);
      setRefreshIntervalId(null);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    isLoading: isLoading || !isInitialized,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
